import { createSelector } from '@reduxjs/toolkit';
import isEqual from 'lodash/isEqual';
import { selectOnlineEngines } from '@common/reducers/ConnectedEnginesSlice';
import { isAlwaysOn } from '@bt-common/notifier/presence';
import { sessionSelectors } from '@common/reducers/DBServiceThunks';
import { DisplayEngine } from './types';
// Single source of truth for the lastSeen-anchored connection status (shared with the top-bar
// indicator's selectIsCurrentUserSessionConnected — keep them DRY so they never diverge).
import { deriveDisplayStatus, CONNECTED_FRESHNESS_MS, selectNow } from '@common/reducers/engine-display-status';

// Re-exported for existing consumers of this module.
export { CONNECTED_FRESHNESS_MS };

export const selectDisplayEngines = createSelector(
  [
    sessionSelectors.selectAll,
    selectOnlineEngines,
    selectNow
  ],
  (allSessions, onlineEngines, now): DisplayEngine[] => {
    const engines: DisplayEngine[] = [];
    const processedSessionIds = new Set<string>();

    // First, add all sessions (gateway engines)
    allSessions.forEach((session) => {
      // Find if this session has a connected socket from MQTT presence.
      // NOTE: this is now used only for domain/connectionTime metadata — NOT to
      // drive the connected/disconnected indicator. MQTT presence is the jittery
      // signal that was causing the flicker; the indicator is anchored to lastSeen
      // (the heartbeat) instead.
      const connectedSocket = onlineEngines.find(isAlwaysOn);

      // Check if this is an old gateway type that should be read-only.
      // Current engine pods are 'session-pod' (Chrome gateway) and 'baileys-pod'
      // (Baileys gateway) — both are first-class new engines. Anything else is a
      // pre-pod legacy gateway. (Baileys pods were wrongly flagged "(Legacy) /
      // Upgrade recommended" and non-restartable when only 'session-pod' passed.)
      const NEW_ENGINE_TYPES = ['session-pod', 'baileys-pod'];
      const isOldGateway = !NEW_ENGINE_TYPES.includes(session.engineType);

      // Displayed status, anchored to the lastSeen heartbeat (shared derivation — see
      // engine-display-status.ts). MQTT presence flickers on transient drops; lastSeen doesn't.
      const status: string = deriveDisplayStatus(session, now);

      engines.push({
        id: session._id,
        type: 'gateway',
        name: isOldGateway ? 'Remote connection (Legacy)' : 'Remote connection',
        status: status,
        domain: 'gateway',
        connectionTime: connectedSocket?.connectionTime || session.updatedAt || session.createdAt,
        canDelete: true, // Allow deletion for both legacy and new engines
        canRestart: !isOldGateway, // Only new engines can be restarted
        session: session,
        qrCode: session.qrcode || session.rawWaStatus?.qrcode || session.qr || '',
        isLegacy: isOldGateway
      });
      processedSessionIds.add(session._id);
    });

    // Then, add WhatsApp Web connections that don't correspond to sessions
    onlineEngines.forEach((engine) => {
      if (engine.engineKind === 'extension') {
        engines.push({
          id: engine.id,
          type: 'whatsapp-web',
          name: 'WhatsApp Web',
          // A logged-out / QR WhatsApp Web is online but can't send: 'unpaired' ("Needs Pairing").
          status: engine.updateRequired ? 'update_required' : engine.waPairing === 'logged_out' ? 'unpaired' : 'connected',
          domain: 'web.whatsapp.com',
          connectionTime: engine.connectionTime,
          canDelete: false,
          canRestart: false,
        });
      }
    });

    // Sort engines by type: gateway first, then whatsapp-web
    return engines.sort((a, b) => {
      const typeOrder = { 'gateway': 0, 'whatsapp': 0, 'whatsapp-web': 1 };
      const aOrder = typeOrder[a.type] ?? 2;
      const bOrder = typeOrder[b.type] ?? 2;

      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }

      // If same type, sort by connection time (most recent first)
      return new Date(b.connectionTime).getTime() - new Date(a.connectionTime).getTime();
    });
  },
  // The `now` tick (30s) forces this selector to recompute, but the DISPLAYED output
  // (statuses, names, connection times) only actually changes when a session crosses the
  // connectedAt freshness boundary or real session/presence data updates. A deep
  // resultEqualityCheck returns the PREVIOUS array reference (with its previous element
  // object references) whenever the recomputed result is value-identical — so a bare wall-
  // clock tick no longer churns fresh engine objects, and memoized <EngineRow>s (which get
  // the same `engine` prop reference) skip re-rendering. Recompute cost is trivial (a
  // handful of engines); the win is downstream render suppression.
  { memoizeOptions: { resultEqualityCheck: isEqual } }
);
